import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, ArrowLeft, CircleNotch } from "@phosphor-icons/react";
import { BACKEND_URL } from "../lib/api";
import axios from "axios";

/**
 * Public payment-status page. Polls /api/payments/status/{session_id} after Stripe redirect.
 */
export function PaySuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
      setDetails(data);
      if (data.payment_status === "paid") setStatus("paid");
      else if (data.status === "expired") setStatus("expired");
      else if (attempts >= 8) setStatus("timeout");
      else setAttempts((a) => a + 1);
    } catch {
      if (attempts >= 8) setStatus("error");
      else setAttempts((a) => a + 1);
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (!sessionId) return;
    if (status !== "checking") return;
    const t = setTimeout(poll, attempts === 0 ? 600 : 2000);
    return () => clearTimeout(t);
  }, [poll, attempts, status, sessionId]);

  if (!sessionId) {
    return (
      <Shell>
        <Card icon={<XCircle size={42} weight="fill" />} tone="error" title="No payment session" subtitle="This link is missing a session id." />
      </Shell>
    );
  }

  if (status === "paid") {
    const amt = (details?.amount_total || 0) / 100;
    return (
      <Shell>
        <Card
          tone="ok"
          icon={<CheckCircle size={48} weight="fill" />}
          title="Payment received"
          subtitle={`We've charged $${amt.toFixed(2)} for your MedexOVA trip. A text receipt is on its way.`}
          testId="pay-success"
        />
      </Shell>
    );
  }
  if (status === "expired" || status === "timeout" || status === "error") {
    return (
      <Shell>
        <Card
          tone="error"
          icon={<XCircle size={42} weight="fill" />}
          title={status === "expired" ? "Payment session expired" : "Couldn't verify payment"}
          subtitle="If you completed payment, watch for the SMS receipt within a minute or two. Otherwise call (833) 659-2699."
          testId="pay-error"
        />
      </Shell>
    );
  }
  return (
    <Shell>
      <Card
        tone="pending"
        icon={<CircleNotch size={42} weight="bold" className="animate-spin" />}
        title="Verifying payment…"
        subtitle="Please don't close this page. We're checking with Stripe."
        testId="pay-pending"
      />
    </Shell>
  );
}

export function PayCancel() {
  return (
    <Shell>
      <Card
        tone="error"
        icon={<XCircle size={42} weight="fill" />}
        title="Payment cancelled"
        subtitle="No charge was made. Call (833) 659-2699 if you'd like to try again."
        testId="pay-cancel"
      />
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center px-4">
      <div aria-hidden className="orb h-[520px] w-[520px] bg-brandblue/30 -top-40 -left-40" />
      <div aria-hidden className="orb h-[400px] w-[400px] bg-ova/25 -bottom-20 -right-10" />
      {children}
    </main>
  );
}

function Card({ icon, title, subtitle, tone = "ok", testId }) {
  const tones = {
    ok: "text-emerald-300",
    error: "text-rose-300",
    pending: "text-brandblue",
  };
  return (
    <div
      data-testid={testId}
      className="relative z-10 max-w-md w-full bg-white/[0.04] border border-white/10 rounded-2xl p-8 text-center backdrop-blur"
    >
      <div className={`mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/[0.06] ring-1 ring-white/10 ${tones[tone]}`}>
        {icon}
      </div>
      <h1 className="font-heading text-3xl font-black">{title}</h1>
      <p className="mt-3 text-white/65 text-sm leading-relaxed">{subtitle}</p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center gap-2 text-brandblue hover:text-white text-xs uppercase tracking-[0.2em] font-bold"
      >
        <ArrowLeft size={12} /> Back to MedexOVA
      </Link>
    </div>
  );
}
